import { Link } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../../api/client.ts';
import {
  useAddAlias, useAddPerson, useClientDetails, useCorrectIdentity, useEndAlias, useEndPerson, useIdentifierHistory,
  useIdentityHistory, usePatchClient, useRemoveIdentifier, useRevealIdentifier, useSetIdentifier,
  type ClientDetails, type IdentifierKind, type Identifiers, type IdentityEvent, type PersonOnCase,
} from '../../api/clientDetails.ts';
import { format, inputModeFor, masked } from '../../identifiers/format.ts';
import { Panel } from '../../components/Panel.tsx';
import { LoadFailure } from '../../components/LoadFailure.tsx';
import { formatDate } from '../../dates.ts';
import { roleNote } from '../../roleNote.ts';

/**
 * Edit details (section 13's first group, Arthur, 15 October 2026), inside the client's record.
 *
 * A CORRECTION AND AN ALIAS ARE DIFFERENT THINGS, and the page makes the choice explicit:
 * - "Correct the name": the name (or date of birth) was recorded wrong. The new one replaces it; the
 *   old value is kept only in the identity history below, shown to those who may correct it, and is
 *   never searched.
 * - "Add a name they go by": a name the person genuinely goes by (a nickname their bills are under).
 *   Shown on the client and found by search. Ended with a reason, never deleted.
 *
 * Each panel shows what the reader may change as fields, and everything else as words, never faded.
 * The server decided what may be changed (`may`) and every route asks again.
 */

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const primary =
  'inline-flex min-h-[44px] items-center rounded-lg bg-neutral-900 px-4 font-semibold text-white active:bg-neutral-700 ' +
  'disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-600';
const secondary =
  'inline-flex min-h-[44px] items-center rounded-lg border border-neutral-400 px-4 font-semibold text-neutral-800 hover:bg-tint';

function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function Refusal({ error }: { error: unknown }) {
  if (!error) return null;
  const e = error instanceof ApiError ? error : null;
  return (
    <p role="alert" className="mt-2 rounded-lg border-l-8 border-critical-edge bg-panel px-3 py-2 text-sm text-critical-edge">
      {e ? e.message : 'That could not be saved.'}
      {e?.hint && <span className="block text-neutral-700">{e.hint}</span>}
    </p>
  );
}

function Fact({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div className="contents">
      <dt className="text-neutral-600">{name}</dt>
      <dd className="text-neutral-900">{children ?? <span className="text-neutral-600 italic">Not recorded</span>}</dd>
    </div>
  );
}

const fullName = (c: { last_name: string | null; first_name: string | null; middle_name: string | null; suffix: string | null }) =>
  `${c.last_name ?? ''}${c.first_name || c.middle_name ? ', ' : ''}${[c.first_name, c.middle_name].filter(Boolean).join(' ')}${c.suffix ? ` ${c.suffix}` : ''}`;

/** The web path of the page that edits the people on the case, and nothing else (A-#97, 23 October). */
export const peopleEditPath = (clientId: string) => `/clients/${clientId}/people`;

/**
 * Edit details: the client's own details. The people on the case are NOT here (Arthur, 23 October,
 * A-#97): they have their own button on the dashboard's People on the case panel and their own page,
 * EditPeople below. Both read the same GET /clients/:id/details, so the server still says what is drawn.
 */
export function EditDetails({ clientId }: { clientId: string }) {
  const q = useClientDetails(clientId);
  if (q.error && !q.data) {
    return <LoadFailure what="Edit details" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />;
  }
  if (!q.data) return <p className="p-6 text-neutral-600">Loading…</p>;
  const d = q.data;
  const own = d.panels.filter((p) => p !== 'people');
  return (
    <div className="grid12">
      <Panel title="Edit details" note={d.client.client_ref} span={12}>
        <p className="px-4 py-3 text-sm text-neutral-700">
          {own.length === 0
            ? 'Nothing here is yours to change.'
            : 'What you may change about this client. Within each part, anything your roles cannot change is shown as text.'}{' '}
          {d.may.people && (
            <>The people on the case are edited from their own page: <Link to={peopleEditPath(clientId)} className="link">Edit people on the case</Link>.{' '}</>
          )}
          <Link to={`/clients/${clientId}`} className="link">Back to the dashboard</Link>
        </p>
      </Panel>
      {/* Only what this reader may change (Arthur, 15 October): the server says which panels. */}
      {own.includes('vital') && <NamePanel clientId={clientId} d={d} />}
      {own.includes('aliases') && <AliasesPanel clientId={clientId} d={d} />}
      {own.includes('court') && <CourtPanel clientId={clientId} d={d} />}
      {own.includes('communication') && <CommunicationPanel clientId={clientId} d={d} />}
      {own.includes('history') && <HistoryPanel clientId={clientId} withIdentifiers={d.identifiers?.may_history === true} />}
    </div>
  );
}

/**
 * Edit people on the case (Arthur, 23 October, A-#97): the people alone, opened from their own button.
 * The same data and the same routes as before (POST /clients/:id/people and .../end, Legal at write);
 * only where they are edited has moved.
 */
export function EditPeople({ clientId }: { clientId: string }) {
  const q = useClientDetails(clientId);
  if (q.error && !q.data) {
    return <LoadFailure what="People on the case" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />;
  }
  if (!q.data) return <p className="p-6 text-neutral-600">Loading…</p>;
  const d = q.data;
  return (
    <div className="grid12">
      <Panel title="Edit people on the case" note={d.client.client_ref} span={12}>
        <p className="px-4 py-3 text-sm text-neutral-700">
          {d.panels.includes('people')
            ? 'The court’s people and counsel on this client’s case. '
            : 'The people on the case are not yours to change: Legal, Compliance, the Director of Social Work and tiers 1–2 record them. '}
          <Link to={`/clients/${clientId}`} className="link">Back to the dashboard</Link>
        </p>
      </Panel>
      {d.panels.includes('people') && <PeopleOnCasePanel clientId={clientId} d={d} />}
    </div>
  );
}

// ---------------------------------------------------------------- correct the name

function NamePanel({ clientId, d }: { clientId: string; d: ClientDetails }) {
  const c = d.client;
  const [open, setOpen] = useState(false);
  const [last, setLast] = useState(c.last_name);
  const [first, setFirst] = useState(c.first_name ?? '');
  const [middle, setMiddle] = useState(c.middle_name ?? '');
  const [suffix, setSuffix] = useState(c.suffix ?? '');
  const [dob, setDob] = useState(c.date_of_birth ?? '');
  const [reason, setReason] = useState('');
  const correct = useCorrectIdentity(clientId);
  const changed = last.trim() !== c.last_name || first.trim() !== (c.first_name ?? '') || middle.trim() !== (c.middle_name ?? '')
    || suffix !== (c.suffix ?? '') || dob !== (c.date_of_birth ?? '');
  const reset = () => {
    setLast(c.last_name); setFirst(c.first_name ?? ''); setMiddle(c.middle_name ?? ''); setSuffix(c.suffix ?? '');
    setDob(c.date_of_birth ?? ''); setReason(''); correct.reset();
  };
  return (
    <Panel title="Vital Information" span={6}
           actions={d.may.identity && !open ? (
             <button type="button" onClick={() => { reset(); setOpen(true); }}
                     className={secondary}>
               Correct the name
             </button>
           ) : undefined}>
      {/* The name and date of birth, for those who may correct them; the identifiers below, for those who may
          see them (120). A reader with only the identifiers sees only those. */}
      {!d.may.identity ? null : !open ? (
        <div className="px-4 py-3 text-[0.9375rem]">
          <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1">
            <Fact name="Name">{c.display_name}</Fact>
            <Fact name="Date of birth">{c.date_of_birth ? formatDate(c.date_of_birth) : null}</Fact>
          </dl>
        </div>
      ) : (
        <form className="grid gap-3 px-4 py-3" onSubmit={(e) => {
          e.preventDefault();
          correct.mutate({
            last_name: last, first_name: first || null, middle_name: middle || null, suffix: suffix || null,
            date_of_birth: dob || null, reason, version: c.version,
          }, { onSuccess: () => setOpen(false) });
        }}>
          <p className="rounded-lg bg-tint px-3 py-2 text-sm text-neutral-800">
            Use this only when the name or date of birth was <strong>recorded wrong</strong>. The corrected name replaces it
            everywhere. The old one is kept in the identity history, not shown on the client and not searched. If this is a
            name the person goes by, use “Add an alias” instead.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={label} htmlFor="fix-last">Surname</label>
              <input id="fix-last" required value={last} onChange={(e) => setLast(e.target.value)} className={field} />
              <p className={hint}>Both surnames where applicable, paternal first. Never a suffix.</p>
            </div>
            <div>
              <label className={label} htmlFor="fix-first">First name</label>
              <input id="fix-first" value={first} onChange={(e) => setFirst(e.target.value)} className={field} />
            </div>
            <div>
              <label className={label} htmlFor="fix-middle">Middle name</label>
              <input id="fix-middle" value={middle} onChange={(e) => setMiddle(e.target.value)} className={field} />
            </div>
            <div>
              <label className={label} htmlFor="fix-suffix">Suffix</label>
              <select id="fix-suffix" value={suffix} onChange={(e) => setSuffix(e.target.value)} className={field}>
                <option value="">None</option>
                {d.suffixes.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={label} htmlFor="fix-dob">Date of birth</label>
              <input id="fix-dob" type="date" max={todayLocal()} value={dob} onChange={(e) => setDob(e.target.value)} className={field} />
            </div>
          </div>
          <div>
            <label className={label} htmlFor="fix-reason">Why it was wrong (required)</label>
            <input id="fix-reason" required value={reason} onChange={(e) => setReason(e.target.value)} className={field}
                   placeholder="For example: misspelt at intake; the order spells it this way" />
          </div>
          <Refusal error={correct.error} />
          <div className="flex flex-wrap gap-3">
            <button type="submit" className={primary} disabled={!changed || !reason.trim() || !last.trim() || correct.isPending}>
              {correct.isPending ? 'Saving…' : 'Save the correction'}
            </button>
            <button type="button" className={secondary} onClick={() => { reset(); setOpen(false); }}>Cancel</button>
          </div>
          {!changed && <p className={hint}>Nothing has been changed yet.</p>}
        </form>
      )}
      {d.identifiers && !open && <IdentifierLines clientId={clientId} ids={d.identifiers} />}
    </Panel>
  );
}

// ---------------------------------------------------------------- the identifiers (120)


/**
 * The SSN, Medicaid ID and Medicare ID, masked. "Show" reveals one for 30 seconds: the database records
 * who, when, which field and which client before the value leaves it. The value lives only in this
 * line's state, never in a cache, and is dropped when hidden.
 */
function IdentifierLines({ clientId, ids }: { clientId: string; ids: Identifiers }) {
  return (
    <div className="border-t border-line px-4 py-3">
      {!ids.available && (
        <p role="status" className="mb-2 rounded-lg border-l-8 border-warning-edge bg-warning-bg px-3 py-2 text-sm text-warning-fg">
          These numbers cannot be read or recorded right now: the key that protects them is unavailable.
        </p>
      )}
      <ul className="divide-y divide-line">
        {ids.identifiers.map((x) => <IdentifierLine key={x.kind} clientId={clientId} x={x} available={ids.available} />)}
      </ul>
      <p className={hint}>Every Show is recorded: who, when and which number.</p>
    </div>
  );
}

function IdentifierLine({ clientId, x, available }: {
  clientId: string; x: Identifiers['identifiers'][number]; available: boolean;
}) {
  const reveal = useRevealIdentifier(clientId);
  const set = useSetIdentifier(clientId);
  const remove = useRemoveIdentifier(clientId);
  const [shown, setShown] = useState<string | null>(null);
  const [form, setForm] = useState<null | 'set' | 'remove'>(null);
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (shown === null) return;
    const t = setTimeout(() => setShown(null), 30_000);
    return () => clearTimeout(t);
  }, [shown]);
  const replacing = x.last4 !== null;
  return (
    <li className="py-2">
      <div className="flex min-h-[44px] flex-wrap items-center gap-x-3 gap-y-1">
        <span className="w-[12rem] shrink-0 text-neutral-600">{x.label}</span>
        <span className="min-w-0 flex-1 font-mono tabular-nums text-neutral-900">
          {x.last4 === null ? <span className="font-sans text-neutral-600 italic">Not recorded</span> : (shown ?? masked(x.kind, x.last4))}
        </span>
        {x.last4 !== null && available && (
          shown === null ? (
            <button type="button" className={secondary} disabled={reveal.isPending}
                    onClick={() => reveal.mutate(x.kind, { onSuccess: (r) => { setShown(r.value); reveal.reset(); } })}>
              Show
            </button>
          ) : (
            <button type="button" className={secondary} onClick={() => setShown(null)}>Hide</button>
          )
        )}
        {x.may_edit && available && form === null && (
          <>
            <button type="button" className={secondary}
                    onClick={() => { setForm('set'); setValue(''); setReason(''); set.reset(); setShown(null); }}>
              {replacing ? 'Change' : 'Add'}
            </button>
            {replacing && (
              <button type="button" className={secondary}
                      onClick={() => { setForm('remove'); setReason(''); remove.reset(); setShown(null); }}>Remove</button>
            )}
          </>
        )}
      </div>
      <Refusal error={reveal.error} />
      {form === 'set' && (
        <form className="mt-2 grid gap-3 rounded-lg border border-line p-3" autoComplete="off" onSubmit={(e) => {
          e.preventDefault();
          set.mutate({ kind: x.kind, value, reason: reason || null }, { onSuccess: () => { setForm(null); setValue(''); } });
        }}>
          <div>
            <label className={label} htmlFor={`id-${x.kind}`}>{x.label}</label>
            <IdentifierInput id={`id-${x.kind}`} kind={x.kind} value={value} onChange={setValue} />
          </div>
          {replacing && (
            <div>
              <label className={label} htmlFor={`id-why-${x.kind}`}>Why it is changing (required)</label>
              <input id={`id-why-${x.kind}`} required value={reason} onChange={(e) => setReason(e.target.value)} className={field} />
            </div>
          )}
          <Refusal error={set.error} />
          <div className="flex flex-wrap gap-3">
            <button type="submit" className={primary} disabled={!value.trim() || (replacing && !reason.trim()) || set.isPending}>
              {set.isPending ? 'Saving…' : `Save the ${x.label}`}
            </button>
            <button type="button" className={secondary} onClick={() => { setForm(null); setValue(''); }}>Cancel</button>
          </div>
        </form>
      )}
      {form === 'remove' && (
        <form className="mt-2 grid gap-3 rounded-lg border border-line p-3" onSubmit={(e) => {
          e.preventDefault();
          remove.mutate({ kind: x.kind, reason }, { onSuccess: () => setForm(null) });
        }}>
          <div>
            <label className={label} htmlFor={`id-rm-${x.kind}`}>Why it is being removed (required)</label>
            <input id={`id-rm-${x.kind}`} required value={reason} onChange={(e) => setReason(e.target.value)} className={field} />
          </div>
          <p className={hint}>It stops being shown. The record that it was held is kept.</p>
          <Refusal error={remove.error} />
          <div className="flex flex-wrap gap-3">
            <button type="submit" className={primary} disabled={!reason.trim() || remove.isPending}>Remove the {x.label}</button>
            <button type="button" className={secondary} onClick={() => setForm(null)}>Cancel</button>
          </div>
        </form>
      )}
    </li>
  );
}


// ---------------------------------------------------------------- names they go by

function AliasesPanel({ clientId, d }: { clientId: string; d: ClientDetails }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState(d.alias_kinds[0]?.code ?? 'Informal');
  const [note, setNote] = useState('');
  const [ending, setEnding] = useState<string | null>(null);
  const [why, setWhy] = useState('');
  const add = useAddAlias(clientId);
  const end = useEndAlias(clientId);
  const kindLabel = (code: string) => d.alias_kinds.find((k) => k.code === code)?.label ?? code;
  return (
    <Panel title="Aliases" span={6}
           actions={d.may.aliases && !adding ? (
             <button type="button" onClick={() => { setAdding(true); add.reset(); setName(''); setNote(''); }}
                     className={secondary}>
               Add an alias
             </button>
           ) : undefined}>
      <div className="px-4 py-3">
        {d.aliases.length === 0 ? (
          <p className="text-sm text-neutral-600">No aliases are recorded.</p>
        ) : (
          <ul className="divide-y divide-line">
            {d.aliases.map((a) => (
              <li key={a.id} className="py-1.5">
                <div className="flex min-h-[44px] flex-wrap items-center gap-x-3">
                  <span className="min-w-0 flex-1 font-medium text-neutral-900">{a.alias_name}</span>
                  <span className="text-sm text-neutral-600">{kindLabel(a.alias_type)}</span>
                  {d.may.aliases && ending !== a.id && (
                    <button type="button" className={secondary} onClick={() => { setEnding(a.id); setWhy(''); end.reset(); }}>
                      No longer goes by this
                    </button>
                  )}
                </div>
                {ending === a.id && (
                  <form className="mt-1 grid gap-2" onSubmit={(e) => {
                    e.preventDefault();
                    end.mutate({ alias_id: a.id, reason: why }, { onSuccess: () => setEnding(null) });
                  }}>
                    <label className={label} htmlFor={`end-${a.id}`}>Why (required)</label>
                    <input id={`end-${a.id}`} required value={why} onChange={(e) => setWhy(e.target.value)} className={field} />
                    <p className={hint}>The name stops being searched. It is kept in the identity history, never deleted.</p>
                    <Refusal error={end.error} />
                    <div className="flex flex-wrap gap-3">
                      <button type="submit" className={primary} disabled={!why.trim() || end.isPending}>End this name</button>
                      <button type="button" className={secondary} onClick={() => setEnding(null)}>Cancel</button>
                    </div>
                  </form>
                )}
              </li>
            ))}
          </ul>
        )}
        {adding && (
          <form className="mt-3 grid gap-3 border-t border-line pt-3" onSubmit={(e) => {
            e.preventDefault();
            add.mutate({ alias_name: name, alias_type: kind, note: note || null }, { onSuccess: () => setAdding(false) });
          }}>
            <p className="rounded-lg bg-tint px-3 py-2 text-sm text-neutral-800">
              An alias is a name the person <strong>genuinely goes by</strong>, such as a nickname their bills are under. It is shown on
              the client and found by search. Not for a misspelling: if the name on record is wrong, correct it instead.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label} htmlFor="alias-name">Name</label>
                <input id="alias-name" required value={name} onChange={(e) => setName(e.target.value)} className={field} />
              </div>
              <div>
                <label className={label} htmlFor="alias-kind">What kind of name</label>
                <select id="alias-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={field}>
                  {d.alias_kinds.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={label} htmlFor="alias-note">Where it is used (optional)</label>
              <input id="alias-note" value={note} onChange={(e) => setNote(e.target.value)} className={field}
                     placeholder="For example: her utility bills" />
            </div>
            <Refusal error={add.error} />
            <div className="flex flex-wrap gap-3">
              <button type="submit" className={primary} disabled={name.trim().length < 2 || add.isPending}>Add the alias</button>
              <button type="button" className={secondary} onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </form>
        )}
        {!d.may.aliases && (
          <p className={hint}>Legal, the paralegal and tiers 1–2 record the names a client goes by.</p>
        )}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------- court and communication (PATCH)

function FieldsPanel({ clientId, d, title, span, fields }: {
  clientId: string; d: ClientDetails; title: string; span: 6 | 12;
  fields: { key: string; name: string; may: boolean; long?: boolean; why?: string }[];
}) {
  const patch = usePatchClient(clientId);
  const initial = Object.fromEntries(fields.map((f) => [f.key, (d.client as unknown as Record<string, string | null>)[f.key] ?? '']));
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [editing, setEditing] = useState(false);
  const anyMay = fields.some((f) => f.may);
  const changed = fields.filter((f) => f.may && values[f.key]!.trim() !== (initial[f.key] ?? ''));
  return (
    <Panel title={title} span={span}
           actions={anyMay && !editing ? (
             <button type="button" onClick={() => { setValues(initial); patch.reset(); setEditing(true); }}
                     className={secondary}>
               Change
             </button>
           ) : undefined}>
      {!editing ? (
        <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1 px-4 py-3 text-[0.9375rem]">
          {fields.map((f) => <Fact key={f.key} name={f.name}>{initial[f.key] || null}</Fact>)}
        </dl>
      ) : (
        <form className="grid gap-3 px-4 py-3" onSubmit={(e) => {
          e.preventDefault();
          const body: Record<string, string | null> = { version: d.client.version };
          for (const f of changed) body[f.key] = values[f.key]!.trim() || null;
          patch.mutate(body, { onSuccess: () => setEditing(false) });
        }}>
          {fields.map((f) => (
            <div key={f.key}>
              <label className={label} htmlFor={`f-${f.key}`}>{f.name}</label>
              {!f.may ? (
                <p className="mt-1 text-neutral-900">{initial[f.key] || 'Not recorded'}
                  <span className={`${hint} block`}>{f.why ?? 'Not yours to change.'}</span></p>
              ) : f.long ? (
                <textarea id={`f-${f.key}`} rows={3} value={values[f.key]} className={`${field} py-2`}
                          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
              ) : (
                <input id={`f-${f.key}`} value={values[f.key]} className={field}
                       onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} />
              )}
            </div>
          ))}
          <Refusal error={patch.error} />
          <div className="flex flex-wrap gap-3">
            <button type="submit" className={primary} disabled={changed.length === 0 || patch.isPending}>
              {patch.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className={secondary} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      )}
    </Panel>
  );
}

function CourtPanel({ clientId, d }: { clientId: string; d: ClientDetails }) {
  const why = 'Legal, the paralegal and tiers 1–2 change the court’s details.';
  return (
    <FieldsPanel clientId={clientId} d={d} title="Court Information" span={6} fields={[
      { key: 'court_name', name: 'Court', may: d.may.court?.court_name === true, why },
      { key: 'court_index_number', name: 'Index number', may: d.may.court?.court_index_number === true, why },
      { key: 'assigned_judge', name: 'Judge', may: d.may.court?.assigned_judge === true, why },
    ]} />
  );
}

function CommunicationPanel({ clientId, d }: { clientId: string; d: ClientDetails }) {
  const why = 'Not open to your roles.';
  return (
    <FieldsPanel clientId={clientId} d={d} title="Language and notes" span={d.panels.includes('court') ? 6 : 12} fields={[
      { key: 'primary_language', name: 'Language', may: d.may.communication, why },
      { key: 'communication_needs', name: 'Communication needs', may: d.may.communication, long: true, why },
      { key: 'notes', name: 'Notes', may: d.may.communication, long: true, why },
    ]} />
  );
}

// ---------------------------------------------------------------- people on the case

/**
 * The lines the people page records, in order. The evaluator and the examiner are never one line. `several`: more
 * than one person may hold the line at once (co-guardians; prior guardians, 148: a client can be succeeded more than
 * once, and each entry is a new contact, as on every line).
 */
const LINES: { code: string; name: string; several?: boolean }[] = [
  { code: 'LEG_COURT_EVAL', name: 'Court evaluator' },
  { code: 'LEG_COURT_EXAM', name: 'Court examiner' },
  { code: 'LEG_ALLIED_COUNSEL', name: 'Attorney for Allied' },
  { code: 'LEG_COUNSEL', name: 'Attorney for the client' },
  { code: 'LEG_GAL', name: 'Guardian ad litem' },
  { code: 'LEG_COGUARDIAN', name: 'Co-guardian', several: true },
  { code: 'LEG_PRIOR_GUARDIAN', name: 'Prior guardian', several: true },
];

/**
 * The prompt on a successor appointment (148, A-#97): asked, in words, beside the prior guardian line. Optional:
 * nothing is required, and nothing else changes.
 */
export const PRIOR_GUARDIAN_PROMPT = 'This is a successor appointment: record the prior guardian, if known.';
const promptFor = (code: string, successor: boolean) =>
  successor && code === 'LEG_PRIOR_GUARDIAN' ? PRIOR_GUARDIAN_PROMPT : null;

function PeopleOnCasePanel({ clientId, d }: { clientId: string; d: ClientDetails }) {
  const people = d.people ?? [];
  const note = (code: string) => d.legal_roles?.find((r) => r.role_code === code)?.notes ?? null;
  return (
    <Panel title="People on the case" span={12}>
      <div className="px-4 py-3">
        <p className="text-sm text-neutral-700">
          The judge is recorded in Edit details, under Court. {d.may.people ? '' : 'Legal, Compliance, the Director of Social Work and tiers 1–2 record the court’s people.'}
        </p>
        <ul className="mt-2 divide-y divide-line">
          {LINES.map((line) => (
            <PersonLine key={line.code} clientId={clientId} line={line} note={note(line.code)} may={d.may.people}
                        prompt={promptFor(line.code, d.successor === true)}
                        current={people.filter((p) => p.role_code === line.code)} />
          ))}
        </ul>
      </div>
    </Panel>
  );
}

function PersonLine({ clientId, line, note, may, prompt, current }: {
  clientId: string; line: { code: string; name: string; several?: boolean }; note: string | null; may: boolean;
  prompt: string | null; current: PersonOnCase[];
}) {
  const [form, setForm] = useState<null | { replaces?: string }>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [appointed, setAppointed] = useState('');
  const add = useAddPerson(clientId);
  const end = useEndPerson(clientId);
  const open = (replaces?: string) => { setForm({ replaces }); setName(''); setPhone(''); setEmail(''); setAppointed(''); add.reset(); };
  return (
    <li className="py-2">
      <div className="grid gap-x-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
        <div>
          <p className="font-semibold text-neutral-900">{line.name}</p>
          {note && <p className="text-xs text-neutral-600">{roleNote(note)}</p>}
          {prompt && <p data-prompt={line.code} className="mt-1 text-sm font-medium text-neutral-900">{prompt}</p>}
        </div>
        <div>
          {current.length === 0 ? (
            <p className="text-neutral-600 italic">Not recorded</p>
          ) : current.map((p) => (
            <div key={p.assignment_id} className="flex min-h-[44px] flex-wrap items-center gap-x-3">
              <span className="min-w-0 flex-1 text-neutral-900">
                {p.name ?? 'Name not recorded'}
                <span className="block text-sm text-neutral-600">{[p.phone, p.email].filter(Boolean).join(' · ')}</span>
              </span>
              {may && (
                <>
                  <button type="button" className={secondary} onClick={() => open(p.assignment_id)}>Change</button>
                  <button type="button" className={secondary} disabled={end.isPending}
                          onClick={() => end.mutate({ link_id: p.assignment_id, reason: null })}>Remove</button>
                </>
              )}
            </div>
          ))}
          {may && !form && (current.length === 0 || line.several) && (
            <button type="button" className={`${secondary} mt-1`} onClick={() => open()}>Add</button>
          )}
          <Refusal error={end.error} />
          {form && (
            <form className="mt-2 grid gap-3 rounded-lg border border-line p-3" onSubmit={(e) => {
              e.preventDefault();
              add.mutate({ role_code: line.code, name, phone: phone || null, email: email || null,
                           appointed_on: appointed || null, note: null, ...(form.replaces ? { replaces: form.replaces } : {}) },
                         { onSuccess: () => setForm(null) });
            }}>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={label} htmlFor={`p-name-${line.code}`}>Name, as Surname, Forename</label>
                  <input id={`p-name-${line.code}`} required value={name} onChange={(e) => setName(e.target.value)} className={field} />
                </div>
                <div>
                  <label className={label} htmlFor={`p-date-${line.code}`}>Appointed on (if known)</label>
                  <input id={`p-date-${line.code}`} type="date" max={todayLocal()} value={appointed}
                         onChange={(e) => setAppointed(e.target.value)} className={field} />
                </div>
                <div>
                  <label className={label} htmlFor={`p-phone-${line.code}`}>Phone</label>
                  <input id={`p-phone-${line.code}`} value={phone} onChange={(e) => setPhone(e.target.value)} className={field} />
                </div>
                <div>
                  <label className={label} htmlFor={`p-email-${line.code}`}>Email</label>
                  <input id={`p-email-${line.code}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
                </div>
              </div>
              {form.replaces && <p className={hint}>The person on this line now is ended today, and this person recorded instead.</p>}
              <Refusal error={add.error} />
              <div className="flex flex-wrap gap-3">
                <button type="submit" className={primary} disabled={name.trim().length < 2 || add.isPending}>
                  {form.replaces ? 'Record the change' : `Record the ${line.name.toLowerCase()}`}
                </button>
                <button type="button" className={secondary} onClick={() => setForm(null)}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------- identity history

/** The only place a corrected name's old value is shown (Arthur, 15 October). For those who may correct identity. */
/**
 * The number's field, formatted as it is typed (16 October): the SSN 123-45-6789, the Medicare ID
 * 1EG4-TE5-MK73, the Medicaid ID upper-cased. Only spaces and dashes are dropped; anything else wrong is
 * kept, so the server's refusal says so. What is sent is what is shown.
 */
export function IdentifierInput({ id, kind, value, onChange }: {
  id: string; kind: IdentifierKind; value: string; onChange: (v: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (caret.current !== null && ref.current && document.activeElement === ref.current) {
      ref.current.setSelectionRange(caret.current, caret.current);
    }
    caret.current = null;
  }, [value]);
  return (
    <input ref={ref} id={id} required value={value} className={`${field} font-mono`} autoComplete="off" spellCheck={false}
           inputMode={inputModeFor(kind)}
           onChange={(e) => {
             const next = format(kind, e.target.value, e.target.selectionStart ?? e.target.value.length, value);
             caret.current = next.caret;
             onChange(next.text);
           }} />
  );
}

function HistoryPanel({ clientId, withIdentifiers }: { clientId: string; withIdentifiers: boolean }) {
  const q = useIdentityHistory(clientId, true);
  const idq = useIdentifierHistory(clientId, withIdentifiers);
  const describe = (h: IdentityEvent) => {
    if (h.kind === 'correction') {
      const was = fullName({ last_name: h.prev_last_name, first_name: h.prev_first_name, middle_name: h.prev_middle_name, suffix: h.prev_suffix });
      const now = fullName({ last_name: h.new_last_name, first_name: h.new_first_name, middle_name: h.new_middle_name, suffix: h.new_suffix });
      const parts: string[] = [];
      if (was !== now) parts.push(`Name corrected from “${was}” to “${now}”`);
      if (h.prev_date_of_birth !== h.new_date_of_birth) {
        parts.push(`Date of birth corrected from ${h.prev_date_of_birth ? formatDate(h.prev_date_of_birth) : 'none'} to ${h.new_date_of_birth ? formatDate(h.new_date_of_birth) : 'none'}`);
      }
      return parts.join('. ');
    }
    return h.kind === 'alias added'
      ? `Alias added: “${h.alias_name}”${h.source ? ` (${h.source})` : ''}`
      : `Alias ended: “${h.alias_name}”`;
  };
  return (
    <Panel title="Identity history" note="Only those who may correct a name see this" span={12}>
      <div className="px-4 py-3">
        {q.error && !q.data ? (
          <LoadFailure what="The identity history" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />
        ) : !q.data ? <p className="text-neutral-600">Loading…</p> : q.data.history.length === 0 ? (
          <p className="text-sm text-neutral-600">No correction has been made and no alias recorded.</p>
        ) : (
          <ul className="divide-y divide-line">
            {q.data.history.map((h, i) => (
              <li key={i} className="py-2 text-[0.9375rem]">
                <p className="text-neutral-900">{describe(h)}</p>
                <p className="text-sm text-neutral-600">
                  {new Date(h.at).toLocaleString()}{h.by_name ? ` · ${h.by_name}` : ''}{h.reason ? ` · ${h.reason}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
        {withIdentifiers && (
          <div className="mt-3 border-t border-line pt-3">
            <h3 className="text-sm font-semibold text-neutral-800">SSN, Medicaid and Medicare IDs: changes and every Show</h3>
            {idq.error && !idq.data ? (
              <LoadFailure what="The identifiers' history" error={idq.error} onRetry={() => void idq.refetch()} retrying={idq.isFetching} forbiddenIsSession={false} />
            ) : !idq.data ? <p className="text-neutral-600">Loading…</p> : idq.data.events.length === 0 ? (
              <p className="text-sm text-neutral-600">No number has been recorded or shown.</p>
            ) : (
              <ul className="divide-y divide-line">
                {idq.data.events.map((e, i) => (
                  <li key={i} className="py-2 text-[0.9375rem]">
                    <p className="text-neutral-900">
                      {ID_LABEL[e.kind]} {e.action}{e.last4 ? ` (ending ${e.last4})` : ''}
                    </p>
                    <p className="text-sm text-neutral-600">
                      {new Date(e.at).toLocaleString()}{e.by_name ? ` · ${e.by_name}` : ''}{e.reason ? ` · ${e.reason}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}

const ID_LABEL: Record<IdentifierKind, string> = { SSN: 'Social Security number', MEDICAID: 'Medicaid ID', MEDICARE: 'Medicare ID' };
